import type { HealthUnitType } from '@/modules/company/Units/unitType'

/** Espelha os enums do backend (pin-suggestion). */
export type PinSuggestionKind = 'PIN_MOVE' | 'RADIUS_REVIEW'
export type PinSuggestionStatus = 'OPEN' | 'APPLIED' | 'DISCARDED' | 'EXPIRED'
export type PinDiscardReason = 'DOCTORS_OFFSITE' | 'PIN_CORRECT' | 'UNIT_INACTIVE' | 'OTHER'
export type PinChangeOrigin = 'PLACES' | 'ADDRESS' | 'LINK' | 'MANUAL' | 'SUGGESTION'
export type PinChangeReason =
  | 'WRONG_ADDRESS'
  | 'DOCTORS_ENTRANCE'
  | 'HEADQUARTERS_MOVED'
  | 'APPLIED_SUGGESTION'
  | 'OTHER'

export const PIN_CHANGE_REASON_LABELS: Record<PinChangeReason, string> = {
  WRONG_ADDRESS: 'Endereço estava errado',
  DOCTORS_ENTRANCE: 'Marcador na entrada usada pelos médicos',
  HEADQUARTERS_MOVED: 'Mudança de sede',
  APPLIED_SUGGESTION: 'Aplicada sugestão dos checks',
  OTHER: 'Outro'
}

export const PIN_DISCARD_REASON_LABELS: Record<PinDiscardReason, string> = {
  DOCTORS_OFFSITE: 'Médicos realizam o check fora da unidade (itinerante/externo)',
  PIN_CORRECT: 'Pino está correto; checks são de GPS impreciso',
  UNIT_INACTIVE: 'Unidade inativa ou em mudança',
  OTHER: 'Outro'
}

/** Uma linha da fila "Revisão da Geolocalização (pino)". */
export interface PinSuggestionItem {
  id: number
  kind: PinSuggestionKind
  status: PinSuggestionStatus
  healthUnit: {
    id: number
    name: string
    type: HealthUnitType | null
    city: string
    state: string
  }
  /** Pino sugerido pelo cluster de checks; `radiusM` nulo quando a sugestão não mexe no raio. */
  suggested: {
    latitude: number
    longitude: number
    radiusM: number | null
  }
  /** Pino atual da unidade no momento do cálculo. */
  current: {
    latitude: number | null
    longitude: number | null
    radiusM: number | null
    addressId: number | null
  }
  distanceToPinM: number
  spreadP90M: number | null
  checksCount: number
  doctorsCount: number
  daysCount: number
  avoidableRejections: number
  windowStart: string
  windowEnd: string
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  discardReason: PinDiscardReason | null
  discardDetail: string | null
}

/** Valores distintos disponíveis para os selects da fila. */
export interface PinSuggestionFilterOptions {
  companies: Array<{ id: number; name: string }>
  states: string[]
}

export interface PinSuggestionsListResponse {
  data: PinSuggestionItem[]
  total: number
  page: number
  limit: number
  /**
   * Opções dos selects de "Empresa" e "UF". Opcional: versões do backend
   * anteriores ao filtro não mandam o campo.
   */
  filters?: PinSuggestionFilterOptions
  counters: {
    open: number
    applied: number
    discarded: number
    expired: number
  }
  kpis: {
    avoidableRejections14d: number
    unitsWithin100mPct: number | null
    unitsRadiusIssue: number
  }
}

/** Retorno do PATCH /healthUnits/:id/pin — o pino já gravado mais a auditoria. */
export interface PinAppliedResponse {
  auditId: number
  movedDistanceM: number
  healthUnit: {
    id: number
    latitude: number
    longitude: number
    reachRadius: number
    type: HealthUnitType | null
    addressId: number | null
  }
}

export const PIN_CHANGE_ORIGIN_LABELS: Record<PinChangeOrigin, string> = {
  PLACES: 'Places',
  ADDRESS: 'Endereço',
  LINK: 'Link',
  MANUAL: 'Manual',
  SUGGESTION: 'Sugestão'
}

/**
 * Uma linha do histórico do pino da unidade (auditoria do PATCH /healthUnits/:id/pin).
 *
 * O backend devolve a lista já ordenada da mais recente para a mais antiga.
 * `userId` nulo significa alteração feita pelo próprio sistema (job/migração).
 */
export interface PinAuditItem {
  id: number
  createdAt: string
  userId: number | null
  userName: string | null
  beforeLatitude: number | null
  beforeLongitude: number | null
  afterLatitude: number | null
  afterLongitude: number | null
  movedDistanceM: number | null
  beforeRadiusM: number | null
  afterRadiusM: number | null
  reason: PinChangeReason | null
  reasonDetail: string | null
  radiusJustification: string | null
  origin: PinChangeOrigin | null
  confirmedOnBuilding: boolean
  suggestionId: number | null
}
